import { useRefreshSession } from '@portal/appLayer/auth/useRefreshSession';
import { routes } from 'constants/routes';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { showNotification } from 'shared/ui/Notification';
import { getShopifyContractId } from 'shared/utils/subscriptionV2Helpers';
import { useQueryGetSubscriptionsV2 } from 'state/internal/getSubscriptions';
import HomeView from './HomeView';
import HomeViewSkeleton from './HomeViewSkeleton/HomeViewSkeleton';
import SepaView from './SepaView';
import { SepaData } from './SepaView/SepaView.types';
import VenmoView from './VenmoView';
import { VenmoData } from './VenmoView/VenmoView.types';

const Home = () => {
  const router = useRouter();
  const { mutateAsync: refreshSession } = useRefreshSession();
  const [isLoadingRedirect, setIsLoadingRedirect] = useState(true);

  const {
    data: subscriptions = [],
    isPending: isLoadingSubscriptions,
    isError: isErrorSubscriptions,
    failureCount: failureCountSubscriptions
  } = useQueryGetSubscriptionsV2();

  const venmoData = useMemo(() => {
    const venmoSubs = subscriptions.filter((sub: any) => !!sub.venmo_email);
    // Explicitly use double assertion to VenmoData
    return venmoSubs[0] as unknown as VenmoData;
  }, [subscriptions]);
  const isVenmoData = useMemo(() => !!venmoData, [venmoData]);

  const sepaData = useMemo(() => {
    const sepaSubs = subscriptions.filter((sub: any) => !!sub.sepa_email);
    return sepaSubs[0] as unknown as SepaData;
  }, [subscriptions]);
  const isSepaData = useMemo(() => !!sepaData, [sepaData]);

  // refetch the customer object on page load to check for any changes in customer data, tags, etc.
  useEffect(() => {
    refreshSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if fetching shopify subscriptions failed and there are no legacy subscriptions
  // display a toast
  useEffect(() => {
    if (isErrorSubscriptions) {
      showNotification('mutationError', {
        title: 'Something went wrong on our end. Please refresh the page or try again later'
      });
    }
  }, [isErrorSubscriptions]);

  const isRootRoute = routes.root === router.pathname;

  useEffect(() => {
    // if user has 1 active subscription, they should redirect to the detail page for that sub
    if (!isLoadingSubscriptions) {
      const activeSubs = subscriptions?.filter((sub) => sub.is_active);
      const hasSingleActiveSub = activeSubs?.length === 1;

      if (hasSingleActiveSub && !isVenmoData && !isSepaData) {
        const id = getShopifyContractId(activeSubs?.[0]?.shopify_contract_id || '');
        const hasOnlyOneSub = hasSingleActiveSub && subscriptions?.length === 1;

        const redirectToSubDetailPage = hasOnlyOneSub || (isRootRoute && hasSingleActiveSub);

        if (id && redirectToSubDetailPage) {
          router.push(routes.subscriptionDetail({ id }));
          return;
        }
      }

      if (isLoadingRedirect) setIsLoadingRedirect(false);

      // if does not meet sub detail redirect conditions, standard redirect from /members -> /members/subscriptions
      if (isRootRoute) router.push(routes.home);
    } else if (isErrorSubscriptions || failureCountSubscriptions) {
      if (isRootRoute) router.push(routes.home);
      if (isLoadingRedirect) setIsLoadingRedirect(false);
    }
  }, [
    isVenmoData,
    isSepaData,
    isLoadingSubscriptions,
    subscriptions,
    router,
    isRootRoute,
    isLoadingRedirect,
    isErrorSubscriptions,
    failureCountSubscriptions
  ]);

  if (
    (isLoadingSubscriptions && !isErrorSubscriptions && failureCountSubscriptions === 0) ||
    isLoadingRedirect ||
    isRootRoute
  ) {
    return <HomeViewSkeleton />;
  }

  if (isVenmoData) return <VenmoView venmoData={venmoData} />;
  if (isSepaData) return <SepaView sepaData={sepaData} />;

  return <HomeView subscriptions={subscriptions} />;
};

export default Home;
